import { useEffect, useState } from 'react';
import {
  ExerciseSpecificDTO,
  ExerciseTypeDTO,
  FirearmSpecificDTO,
  ExerciseSpecificTargetDTO,
} from '../../services/apiClient';
import FormField from '../common/FormField';
import Button from '../common/Button';
import { Download, Link as LinkIcon } from 'lucide-react';
import Loader from '../common/Loader';
import { useExerciseTargetApi } from '../../hooks/useExerciseTargetApi';
import { showAlert } from '../../utils/sweetAlertUtils';
import { downloadFileFromUrl } from '../../utils/downloadUtils';

interface DownloadPostalTargetsProps {
  firearmSpecific?: FirearmSpecificDTO;
  isLoading: boolean;
  onClose: () => void;
}

const DownloadPostalTargets: React.FC<DownloadPostalTargetsProps> = ({
  firearmSpecific,
  isLoading,
  onClose,
}) => {
  const [exerciseTypeId, setExerciseTypeId] = useState<string>('');
  const [exerciseSpecificId, setExerciseSpecificId] = useState<string>('');
  const [exerciseTypes, setExerciseTypes] = useState<ExerciseTypeDTO[]>([]);
  const [exerciseSpecifics, setExerciseSpecifics] = useState<
    ExerciseSpecificDTO[]
  >([]);
  const [selectedTarget, setSelectedTarget] =
    useState<ExerciseSpecificDTO | null>(null);
  const [exerciseTargets, setExerciseTargets] = useState<
    ExerciseSpecificTargetDTO[]
  >([]);
  const [downloadingTargetId, setDownloadingTargetId] = useState<string | null>(null);

  const { downloadPostalTarget } = useExerciseTargetApi();

  // Extract exercise types from the nested data
  useEffect(() => {
    if (firearmSpecific?.exercises) {
      const types = firearmSpecific.exercises
        .filter((ex: any) => ex.exerciseType?.isPostal)
        .map((ex: any) => ex.exerciseType);
      setExerciseTypes(types);
    } else {
      setExerciseTypes([]);
    }
  }, [firearmSpecific]);

  // When exercise type changes, filter the exercise specifics
  useEffect(() => {
    if (exerciseTypeId && firearmSpecific?.exercises) {
      const selectedExercise = firearmSpecific.exercises.find(
        (ex: any) => ex.exerciseTypeId === exerciseTypeId,
      );
      const specifics = (selectedExercise?.exerciseSpecifics || []).filter(
        (specific: ExerciseSpecificDTO) =>
          (specific.isComplianceHunt === true || specific.isComplianceSport === true) &&
          !specific.isCompetition,
      );
      setExerciseSpecifics(specifics);
      setExerciseSpecificId('');
      setSelectedTarget(null);
      setExerciseTargets([]);
    } else {
      setExerciseSpecifics([]);
      setExerciseSpecificId('');
      setSelectedTarget(null);
      setExerciseTargets([]);
    }
  }, [exerciseTypeId, firearmSpecific]);

  // When exercise specific changes, set the selected target and extract targets
  useEffect(() => {
    if (exerciseSpecificId) {
      const target = exerciseSpecifics.find(
        (ex) => ex.id === exerciseSpecificId,
      );
      setSelectedTarget(target || null);
      setExerciseTargets(target?.exerciseSpecificTargets || []);
    } else {
      setSelectedTarget(null);
      setExerciseTargets([]);
    }
  }, [exerciseSpecificId, exerciseSpecifics]);

  const handleDownloadTarget = async (target: ExerciseSpecificTargetDTO) => {
    if (!target.exerciseTarget?.id) {
      await showAlert({
        icon: 'error',
        title: 'Error',
        text: 'Target information is missing',
      });
      return;
    }

    setDownloadingTargetId(target.exerciseTarget.id);
    try {
      // Generate postal target PDF with date stamp
      const sasUri = await downloadPostalTarget(target.exerciseTarget.id);
      if (sasUri) {
        // Download the PDF (handles mobile/PWA automatically)
        await downloadFileFromUrl(
          sasUri,
          `postal-target-${target.exerciseTarget.code}.pdf`,
          () => {
            // Success callback
            console.log('Target downloaded successfully');
          },
          async (error) => {
            // Error callback
            await showAlert({
              icon: 'error',
              title: 'Download Failed',
              text: 'Failed to download postal target PDF',
            });
          }
        );
      }
    } catch (error: any) {
      await showAlert({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to generate postal target PDF',
      });
    } finally {
      setDownloadingTargetId(null);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!firearmSpecific) {
    return (
      <div className="text-center py-8 text-gray-500">
        No firearm data available
      </div>
    );
  }

  return (
    <>
      <FormField
        id="exerciseTypeId"
        label="Exercise Type"
        value={exerciseTypeId}
        type="dropdown"
        onChange={(e) => setExerciseTypeId(e.target.value)}
        placeholder="Select Exercise Type"
        options={exerciseTypes.map((type) => ({
          value: type.id,
          label: type.title || '',
        }))}
        isLoading={false}
      />
      <FormField
        id="exerciseSpecificId"
        label="Exercise Specific"
        value={exerciseSpecificId}
        type="dropdown"
        onChange={(e) => setExerciseSpecificId(e.target.value)}
        placeholder="Select Exercise Specific"
        options={exerciseSpecifics.map((specific) => ({
          value: specific.id,
          label: specific.title || '',
        }))}
        isLoading={false}
      />

      {selectedTarget && (
        <>
          {exerciseTargets.length > 0 && (
            <div className="mt-4">
              {exerciseTargets.map((target) => (
                <>
                  <div
                    key={target.exerciseTargetId}
                    className="flex items-center justify-between p-4 mb-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        Shooting Target (Rules)
                      </h4>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="primary"
                        onClick={() =>
                          downloadFileFromUrl(
                            target.exerciseTarget!.rulesUrl!,
                            'shooting-rules.pdf'
                          )
                        }
                      >
                        <LinkIcon size={16} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 my-5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {`Shooting Target (${target.exerciseTarget?.code})`}
                      </h4>
                    </div>

                    <Button
                      variant="primary"
                      onClick={() => handleDownloadTarget(target)}
                      disabled={!target.exerciseTarget?.isActive || downloadingTargetId === target.exerciseTarget?.id}
                    >
                      {downloadingTargetId === target.exerciseTarget?.id ? 'Generating...' : <Download size={16} />}
                    </Button>
                  </div>
                </>
              ))}
            </div>
          )}
        </>
      )}

      <div className="flex justify-end space-x-4 mt-6">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
};

export default DownloadPostalTargets;
